import React, { useMemo, useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  getProductUnitOfMeasure,
  getProductQtyStepUnitLabel,
  buildWpaPerUnitPriceLines,
  shouldLockWpaUnitStepOfOne,
  resolveFastQtyStepSize,
  formatQtyStepForDisplay,
} from './wpaProductUtils';
import { wpaLocalizedName } from './wpaCommerceFetch';

const QTY_GRID_CENTER_W = 92;
const QTY_GRID_BTN_H = 52;
const QTY_BTN_PX = 24;
const QTY_DISPLAY_PX = 24;

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  box: { width: '100%', maxWidth: 420, background: '#fff', borderRadius: 12, overflow: 'hidden' },
  header: { padding: '14px 16px 6px', textAlign: 'center' },
  name: { fontSize: 24, fontWeight: 700, color: '#111', lineHeight: 1.25 },
  priceBlock: { marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' },
  price: { fontSize: 24, fontWeight: 600, color: '#111', lineHeight: 1.25 },
  qtyGrid: {
    display: 'grid',
    gridTemplateColumns: `1fr ${QTY_GRID_CENTER_W}px 1fr`,
    gridTemplateRows: `repeat(2, ${QTY_GRID_BTN_H}px)`,
    margin: '14px 16px',
    border: '1px solid #ddd',
    boxSizing: 'border-box',
  },
  qtyGridBtn: {
    margin: 0,
    padding: 0,
    border: 'none',
    borderRight: '1px solid #ddd',
    borderBottom: '1px solid #ddd',
    borderRadius: 0,
    background: '#fff',
    color: '#111',
    cursor: 'pointer',
    fontSize: QTY_BTN_PX,
    fontWeight: 700,
    lineHeight: 1.2,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    WebkitAppearance: 'none',
    appearance: 'none',
  },
  qtyGridBtnRight: { borderRight: 'none' },
  qtyGridBtnBottom: { borderBottom: 'none' },
  qtyGridBtnUnitMinus: { gridColumn: 1, gridRow: 1 },
  qtyGridBtnStepMinus: { gridColumn: 1, gridRow: 2 },
  qtyGridBtnUnitPlus: { gridColumn: 3, gridRow: 1 },
  qtyGridBtnStepPlus: { gridColumn: 3, gridRow: 2 },
  qtyGridCenter: {
    gridColumn: 2,
    gridRow: '1 / 3',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRight: '1px solid #ddd',
    color: '#111',
    fontSize: QTY_DISPLAY_PX,
    fontWeight: 700,
    lineHeight: 1.2,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    padding: '0 4px',
    minWidth: 0,
    boxSizing: 'border-box',
  },
  qtyBtnDisabled: { opacity: 0.45, cursor: 'not-allowed' },
  newOrderBtn: { margin: '0 16px 10px', width: 'calc(100% - 32px)', height: 42, border: '1px solid #ddd', background: '#fff', fontSize: 18, fontWeight: 600, cursor: 'pointer' },
  bottom: { display: 'grid', gridTemplateColumns: '1fr 1fr', borderTop: '1px solid #eee' },
  cancelBtn: { height: 50, border: 'none', background: '#fff', color: '#007aff', fontSize: 20, fontWeight: 700, cursor: 'pointer' },
  addBtn: { height: 50, border: 'none', background: '#fff', color: '#007aff', fontSize: 20, fontWeight: 700, cursor: 'pointer', borderLeft: '1px solid #eee' },
};

export default function WpaAddToCartModal({ open, product, onClose, onAddToCurrent, onAddToNew }) {
  const { t, currentLanguage } = useLanguage();
  const [qty, setQty] = useState(0);

  const stepSize = useMemo(() => resolveFastQtyStepSize(product), [product]);
  const fastStep = stepSize != null ? stepSize : 10;

  useEffect(() => {
    if (open && product) {
      setQty(0);
    }
  }, [open, product?.id]);

  /** 標題下價錢列：每「數量單位」＋每「散裝單位」（依登入語系） */
  const priceLines = useMemo(
    () => buildWpaPerUnitPriceLines(product, currentLanguage, t),
    [product, currentLanguage, t],
  );

  const priceUnitLabel = useMemo(() => {
    const u = getProductUnitOfMeasure(product, currentLanguage);
    return u || t('wpa.addToCart.defaultUnit');
  }, [product, currentLanguage, t]);

  /** 快速加減列中央「數量＋單位」：散裝單位（依登入語系）；未設定則與數量單位相同 */
  const qtyStepUnitLabel = useMemo(
    () => getProductQtyStepUnitLabel(product, currentLanguage, priceUnitLabel),
    [product, currentLanguage, priceUnitLabel],
  );

  if (!open || !product) return null;

  const stepLabel = formatQtyStepForDisplay(fastStep);
  const lockUnitStep = shouldLockWpaUnitStepOfOne(product);

  const qtyGridBtnStyle = (disabled, edge) => ({
    ...styles.qtyGridBtn,
    ...(edge?.right ? styles.qtyGridBtnRight : {}),
    ...(edge?.bottom ? styles.qtyGridBtnBottom : {}),
    ...(disabled ? styles.qtyBtnDisabled : {}),
  });

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.box} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.name}>{wpaLocalizedName(product)}</div>
          <div style={styles.priceBlock}>
            {priceLines.map((line, idx) => (
              <div key={idx} style={styles.price}>{line}</div>
            ))}
          </div>
        </div>
        <div style={styles.qtyGrid}>
          <button
            type="button"
            style={{ ...qtyGridBtnStyle(lockUnitStep, {}), ...styles.qtyGridBtnUnitMinus }}
            disabled={lockUnitStep}
            aria-disabled={lockUnitStep}
            onClick={() => {
              if (lockUnitStep) return;
              setQty((q) => Math.max(0, q - 1));
            }}
          >
            {t('wpa.addToCart.unitStepMinus')}
          </button>
          <button
            type="button"
            style={{ ...qtyGridBtnStyle(false, { bottom: true }), ...styles.qtyGridBtnStepMinus }}
            onClick={() =>
              setQty((q) => {
                if (stepSize != null) return Math.max(0, q - stepSize);
                return Math.max(0, q - 10);
              })
            }
          >
            {t('wpa.addToCart.fastStepMinus', { step: stepLabel })}
          </button>
          <span style={styles.qtyGridCenter}>
            {t('wpa.addToCart.qtyWithUnitFmt', { qty, unit: qtyStepUnitLabel })}
          </span>
          <button
            type="button"
            style={{ ...qtyGridBtnStyle(lockUnitStep, { right: true }), ...styles.qtyGridBtnUnitPlus }}
            disabled={lockUnitStep}
            aria-disabled={lockUnitStep}
            onClick={() => {
              if (lockUnitStep) return;
              setQty((q) => q + 1);
            }}
          >
            {t('wpa.addToCart.unitStepPlus')}
          </button>
          <button
            type="button"
            style={{ ...qtyGridBtnStyle(false, { right: true, bottom: true }), ...styles.qtyGridBtnStepPlus }}
            onClick={() =>
              setQty((q) => {
                if (stepSize != null) return q + stepSize;
                return q + 10;
              })
            }
          >
            {t('wpa.addToCart.fastStepPlus', { step: stepLabel })}
          </button>
        </div>
        <button
          type="button"
          style={{ ...styles.newOrderBtn, ...(qty <= 0 ? styles.qtyBtnDisabled : {}) }}
          disabled={qty <= 0}
          aria-disabled={qty <= 0}
          onClick={() => { if (qty > 0) onAddToNew?.(qty); }}
        >
          {t('wpa.addToCart.createNewOrder')}
        </button>
        <div style={styles.bottom}>
          <button type="button" style={styles.cancelBtn} onClick={onClose}>{t('wpa.addToCart.cancel')}</button>
          <button
            type="button"
            style={{ ...styles.addBtn, ...(qty <= 0 ? styles.qtyBtnDisabled : {}) }}
            disabled={qty <= 0}
            aria-disabled={qty <= 0}
            onClick={() => { if (qty > 0) onAddToCurrent?.(qty); }}
          >
            {t('wpa.addToCart.addToCart')}
          </button>
        </div>
      </div>
    </div>
  );
}
